import * as React from 'react';
import { useState } from 'react';
import { format } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';
import Badge from '../../global/Badge';
import { CustomerOverview } from '../../../types/customers';
import { getFullName } from '../../../helpers/global';
import {
  formatAddress,
  formatPhone,
  getCleanedPhone,
} from '../../../utils/global/helper';

interface Props {
  customer: CustomerOverview;
}

const LINK_SX = {
  display: 'inline-block',
  mt: '5px',
  overflowWrap: 'anywhere',
  color: 'primary.main',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

const TEXT_SX = {
  color: 'text.primary',
  mt: '5px',
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
} as const;

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function formatCustomerAddress(
  address: Pick<
    CustomerOverview,
    'streetAddress' | 'streetAddress2' | 'city' | 'state' | 'zip'
  >,
) {
  if (!address.streetAddress || !address.city) return null;
  return formatAddress(
    [
      address.streetAddress,
      address.streetAddress2,
      `${address.city}, ${address.state ?? ''} ${address.zip ?? ''}`.trim(),
    ],
    true,
  );
}

function CustomerContactInfo({ customer }: Props) {
  const [showMore, setShowMore] = useState(false);
  const cleanedPhone = getCleanedPhone(customer.user.phoneNumber || '');
  const phone = cleanedPhone ? formatPhone(cleanedPhone) : null;
  const dobDateStr = customer.dob
    ? new Date(customer.dob).toISOString().split('T')[0]
    : null;
  const dob = dobDateStr
    ? format(new Date(`${dobDateStr}T00:00:00`), 'MM/dd/yyyy')
    : null;
  const igHandle = customer.user.igHandle?.replace(/^@/, '') || null;

  const address = formatCustomerAddress(customer);
  const emergencyContact = customer.emergencyContacts?.[0];
  const emergencyContactText = emergencyContact
    ? formatAddress(
        [
          getFullName(emergencyContact),
          emergencyContact.phoneNumber
            ? formatPhone(emergencyContact.phoneNumber)
            : '',
          emergencyContact.email,
          formatCustomerAddress(emergencyContact),
        ],
        true,
      )
    : null;
  const campaigns = customer.campaignAttributions ?? [];
  const hasMore = !!address || !!emergencyContactText || campaigns.length > 0;

  return (
    <Stack gap="22px" sx={{ mt: '28px' }}>
      {phone && (
        <Field label="Phone">
          <Typography
            variant="body1"
            component="a"
            href={`tel:${cleanedPhone}`}
            sx={LINK_SX}>
            {phone}
          </Typography>
        </Field>
      )}
      {customer.user.email && (
        <Field label="Email">
          <Typography
            variant="body1"
            component="a"
            href={`mailto:${customer.user.email}`}
            sx={LINK_SX}>
            {customer.user.email}
          </Typography>
        </Field>
      )}
      {igHandle && (
        <Field label="Instagram">
          <Typography
            variant="body1"
            component="a"
            href={`https://www.instagram.com/${igHandle}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={LINK_SX}>
            @{igHandle}
          </Typography>
        </Field>
      )}
      {dob && (
        <Field label="Birthday">
          <Typography variant="body1" sx={TEXT_SX}>
            {dob}
          </Typography>
        </Field>
      )}
      {customer.internalNotes && (
        <Field label="Notes">
          <Typography variant="body1" sx={{ ...TEXT_SX, lineHeight: 1.55 }}>
            {customer.internalNotes}
          </Typography>
        </Field>
      )}
      {hasMore && showMore && (
        <>
          {address && (
            <Field label="Address">
              <Typography variant="body1" sx={TEXT_SX}>
                {address}
              </Typography>
            </Field>
          )}
          {emergencyContactText && (
            <Field label="Emergency contact">
              <Typography variant="body1" sx={TEXT_SX}>
                {emergencyContactText}
              </Typography>
            </Field>
          )}
          {campaigns.length > 0 && (
            <Field label="Campaigns">
              <Stack
                direction="row"
                flexWrap="wrap"
                gap="6px"
                sx={{ mt: '5px' }}>
                {campaigns.map(campaign => (
                  <Badge
                    key={campaign.id}
                    text={campaign.name}
                    colorScheme="primary"
                    rounded
                  />
                ))}
              </Stack>
            </Field>
          )}
        </>
      )}
      {hasMore && (
        <Typography
          variant="body2"
          component="button"
          type="button"
          onClick={() => setShowMore(open => !open)}
          sx={{
            alignSelf: 'flex-start',
            p: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: 'text.secondary',
            textDecoration: 'underline',
          }}>
          {showMore ? 'Show less' : 'Show more details'}
        </Typography>
      )}
    </Stack>
  );
}

export default CustomerContactInfo;
